/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IRefinerItemData, RefinerItemType } from '@msdyn365-commerce/components';
import { BusinessPartner, BusinessPartnerUser, ChannelIdentity } from '@msdyn365-commerce/retail-proxy';

import { IInvoicesListResources } from '../invoices-list.props.autogenerated';
import { DateRefinerOptionItem, InvoiceRefinerSourceType, InvoiceStatusRefinerOptionItem } from './refiner-option';

type InvoiceDateRefinerSourceType = InvoiceRefinerSourceType.InvoiceDate | InvoiceRefinerSourceType.DueDate;

/**
 * Parses a date-refiner input value into a Date in the shopper's local time zone.
 *
 * The customized date refiner is backed by an `<input type='date'>`, whose value is always `YYYY-MM-DD`
 * regardless of locale. Passing that string straight to `new Date()` parses it as UTC midnight, which lands on
 * the previous calendar day in negative-offset time zones (the classic off-by-one bug). Parsing the individual
 * parts avoids both the locale sensitivity and the time-zone shift. Any value that is not a valid `YYYY-MM-DD`
 * date returns `undefined` so callers can skip applying an invalid filter.
 * @param dateString - The refiner value, e.g. `2024-01-31` (a leading date part with time, e.g. an ISO string, is also accepted).
 * @returns A local-time `Date` at midnight, or `undefined` when the input is missing or malformed.
 */
export function getDateFromString(dateString?: string): Date | undefined {
    if (!dateString) {
        return undefined;
    }

    const parts = dateString.split('T')[0].split('-');
    if (parts.length !== 3) {
        return undefined;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return undefined;
    }

    const parsedDate = new Date(year, month - 1, day);

    // Guard against overflow (e.g. 2024-02-31 rolling over to March).
    if (parsedDate.getFullYear() !== year || parsedDate.getMonth() !== month - 1 || parsedDate.getDate() !== day) {
        return undefined;
    }

    return parsedDate;
}

/**
 * Maps a channel identity into refiner item data for the invoice origin refiner.
 * @param channelIdentity - The channel identity to map.
 * @returns The refiner item data.
 */
export function mapChannelIdentityIntoIRefinerItemData(channelIdentity?: ChannelIdentity): IRefinerItemData {
    return {
        sourceTypeValue: InvoiceRefinerSourceType.Origin,
        label: channelIdentity?.Name ?? channelIdentity?.RecordId?.toString() ?? '',
        value: channelIdentity?.RecordId?.toString(),
        refinerItemId: channelIdentity?.RecordId ?? 0,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

/**
 * Maps a date refiner option into refiner item data for the invoice-date or due-date refiner.
 * @param dateRefinerOptionItem - The date refiner option to map.
 * @param sourceTypeValue - The refiner source type (invoice date or due date).
 * @param resources - The module resources used for labels.
 * @returns The refiner item data.
 */
export function mapDateRefinerOptionIntoIRefinerItemData(
    dateRefinerOptionItem: DateRefinerOptionItem,
    sourceTypeValue: InvoiceDateRefinerSourceType,
    resources: IInvoicesListResources
): IRefinerItemData {
    let itemData: Pick<IRefinerItemData, 'label' | 'itemType' | 'minLabel' | 'maxLabel'>;

    switch (dateRefinerOptionItem) {
        case DateRefinerOptionItem.LastSevenDays:
            itemData = {
                label: resources.lastSevenDaysDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            };
            break;
        case DateRefinerOptionItem.LastThirtyDays:
            itemData = {
                label: resources.lastThirtyDaysDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            };
            break;
        case DateRefinerOptionItem.LastThreeMonths:
            itemData = {
                label: resources.lastThreeMonthsDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            };
            break;
        case DateRefinerOptionItem.Customized:
            itemData = {
                label: resources.customizedDateRefinerItemLabel,
                minLabel: resources.customizedStartDateRefinerItemLabel,
                maxLabel: resources.customizedEndDateRefinerItemLabel,
                itemType: RefinerItemType.DateRange
            };
            break;
        default:
            throw new Error(`Invalid date refiner option: ${dateRefinerOptionItem}`);
    }

    return {
        ...itemData,
        sourceTypeValue,
        value: dateRefinerOptionItem.toString(),
        refinerItemId: dateRefinerOptionItem,
        isChecked: false
    };
}

/**
 * Maps an invoice status refiner option into refiner item data for the invoice status refiner.
 * @param invoiceStatusRefinerOptionItem - The invoice status refiner option to map.
 * @param resources - The module resources used for labels.
 * @returns The refiner item data.
 */
export function mapInvoiceStatusRefinerOptionIntoIRefinerItemData(
    invoiceStatusRefinerOptionItem: InvoiceStatusRefinerOptionItem,
    resources: IInvoicesListResources
): IRefinerItemData {
    let label: string;

    switch (invoiceStatusRefinerOptionItem) {
        case InvoiceStatusRefinerOptionItem.Unpaid:
            label = resources.invoiceStatusUnpaid;
            break;
        case InvoiceStatusRefinerOptionItem.PartiallyPaid:
            label = resources.invoiceStatusPartiallyPaid;
            break;
        case InvoiceStatusRefinerOptionItem.Paid:
            label = resources.invoiceStatusPaid;
            break;
        default:
            throw new Error(`Invalid invoice status refiner option: ${invoiceStatusRefinerOptionItem}`);
    }

    return {
        sourceTypeValue: InvoiceRefinerSourceType.Status,
        label,
        value: invoiceStatusRefinerOptionItem.toString(),
        refinerItemId: invoiceStatusRefinerOptionItem,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

/**
 * Maps a business partner organization into refiner item data for the organization refiner.
 * @param org - The business partner organization to map.
 * @returns The refiner item data.
 */
export function mapOrganizationIntoIRefinerItemData(org?: BusinessPartner): IRefinerItemData {
    const refinerId = Number(org?.BusinessPartnerId) || 0;
    const organizationCustomerAccountNumber = org?.OrganizationCustomerAccountNumber ?? '';
    const name = org?.Name ?? '';

    return {
        sourceTypeValue: InvoiceRefinerSourceType.Organization,
        label: name || organizationCustomerAccountNumber,
        value: organizationCustomerAccountNumber,
        refinerItemId: refinerId,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

/**
 * Maps a group of business partner users that share a party record id into a single contact refiner item.
 * @param contacts - The business partner users belonging to the same contact.
 * @returns The refiner item data with comma-separated contact person ids.
 */
export function mapContactGroupIntoIRefinerItemData(contacts: BusinessPartnerUser[]): IRefinerItemData {
    const first = contacts[0];
    const refinerId = Number(first?.PartyRecordId) || 0;
    const contactPersonName = `${first?.FirstName ?? ''} ${first?.LastName ?? ''}`;
    const contactPersonIds = contacts
        .map(contact => contact.ContactPersonId)
        .filter((contactPersonId): contactPersonId is string => !!contactPersonId)
        .join(',');

    return {
        sourceTypeValue: InvoiceRefinerSourceType.Contact,
        label: contactPersonName,
        value: contactPersonIds,
        refinerItemId: refinerId,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

/**
 * Deduplicates business partner users by party record id and maps each group into a contact refiner item.
 * @param contacts - The business partner users to group and map.
 * @returns The deduplicated contact refiner items.
 */
export function mapContactsIntoDeduplicatedRefinerItems(contacts: BusinessPartnerUser[]): IRefinerItemData[] {
    const groupedByPartyRecordId = new Map<number, BusinessPartnerUser[]>();

    for (const contact of contacts) {
        const partyRecordId = contact.PartyRecordId ?? 0;
        if (partyRecordId === 0) {
            continue;
        }

        const group = groupedByPartyRecordId.get(partyRecordId) ?? [];
        group.push(contact);
        groupedByPartyRecordId.set(partyRecordId, group);
    }

    return Array.from(groupedByPartyRecordId.values()).map(mapContactGroupIntoIRefinerItemData);
}
